// Client-side state machine for the Raspberry Pi /provision flow.
//
// Pure browser code, no React. The /provision page consumes events from
// `runPairing()` and renders the current step. Tests can drive the state
// machine directly without a DOM.
//
// Lifecycle:
//
//   start  ──► display code/secret  ──► poll loop  ──► completed: persist
//   token + redirect to /play/:screenId
//
//   any of: cancelled / expired / network failures ──► restart (with
//   exponential backoff for hard failures)

import { writeStoredToken } from '@/lib/player-token-storage';
import { buildPlayerUrl as sharedBuildPlayerUrl } from '@/lib/player-url';
import { supabase } from '@/lib/supabase';

const SESSION_STORAGE_KEY = 'signage:pairing-session';
const POLL_INTERVAL_MS = 3000;
const FETCH_TIMEOUT_MS = 8_000;
const MAX_BACKOFF_MS = 30_000;

export const PAIR_BASE_PATH = '/api/player/pair';

export type PairingStatus =
  | 'starting'
  | 'awaiting'
  | 'claimed'
  | 'completed'
  | 'expired'
  | 'cancelled'
  | 'failed';

export interface StartedSession {
  id: string;
  code: string;
  pickupSecret: string;
  expiresAt: string | null;
  startedAt: number;
}

export interface PairingState {
  status: PairingStatus;
  session: StartedSession | null;
  error: string | null;
  /** Last error message we want to surface in the UI (network failure, etc). */
  lastIssue: string | null;
  /** Result of a successful pairing. */
  result: PairingResult | null;
  /** When >0, UI can display a countdown until the next poll/restart. */
  retryAtMs: number | null;
}

export interface PairingResult {
  screenId: string;
  playerId: string | null;
  tenantId: string | null;
  token: string;
}

export interface RunOptions {
  /** Free-form label sent up at start (e.g. hostname). */
  deviceLabel?: string | null;
  /** Free-form info blob sent up at start. */
  deviceInfo?: Record<string, unknown> | null;
  /** Called every time `state` changes. */
  onState: (state: PairingState) => void;
  /** AbortSignal: when fired, cancels in-flight polling cleanly. */
  signal?: AbortSignal;
  /** Override poll interval (tests). */
  pollIntervalMs?: number;
}

export interface PairingHandle {
  /** Stops polling and clears the displayed code. */
  stop(): Promise<void>;
  /** Forces a brand new pairing session (e.g. user pressed "New code"). */
  restart(): Promise<void>;
}

interface StartResponse {
  id: string;
  code: string;
  pickupSecret: string;
  expiresAt: string | null;
}

type ServerPollStatus =
  | 'pending'
  | 'claimed'
  | 'completed'
  | 'expired'
  | 'cancelled'
  | 'invalid';

interface PollResponse {
  status: ServerPollStatus;
  screenId?: string | null;
  tenantId?: string | null;
  playerId?: string | null;
  token?: string;
}

interface CancelResponse {
  status: 'cancelled' | 'completed' | 'expired' | 'invalid';
}

export function readPersistedSession(): StartedSession | null {
  try {
    const raw = sessionStorage.getItem(SESSION_STORAGE_KEY);
    if (!raw) return null;
    const parsed = JSON.parse(raw) as StartedSession;
    if (!parsed?.id || !parsed?.code || !parsed?.pickupSecret) return null;
    return parsed;
  } catch {
    return null;
  }
}

function persistSession(session: StartedSession | null): void {
  try {
    if (session) sessionStorage.setItem(SESSION_STORAGE_KEY, JSON.stringify(session));
    else sessionStorage.removeItem(SESSION_STORAGE_KEY);
  } catch {
    /* ignore */
  }
}

/**
 * Re-exported for callers that already import from this module.
 * The canonical implementation lives in `@/lib/player-url` so it can be
 * unit-tested without pulling in the rest of the pairing state machine.
 */
export const buildPlayerUrl = sharedBuildPlayerUrl;

/**
 * Drive a pairing session end-to-end. Returns a handle for programmatic
 * control. The underlying state is delivered exclusively via `onState`.
 */
export function runPairing(opts: RunOptions): PairingHandle {
  const pollIntervalMs = opts.pollIntervalMs ?? POLL_INTERVAL_MS;
  let stopped = false;
  let activeTimer: ReturnType<typeof setTimeout> | null = null;
  let backoffMs = pollIntervalMs;
  let session: StartedSession | null = null;

  const state: PairingState = {
    status: 'starting',
    session: null,
    error: null,
    lastIssue: null,
    result: null,
    retryAtMs: null,
  };

  function emit(patch: Partial<PairingState>) {
    Object.assign(state, patch);
    opts.onState({ ...state });
  }

  function clearTimer() {
    if (activeTimer) {
      clearTimeout(activeTimer);
      activeTimer = null;
    }
  }

  async function startSession(): Promise<void> {
    emit({
      status: 'starting',
      session: null,
      error: null,
      retryAtMs: null,
      result: null,
    });
    persistSession(null);
    try {
      const res = await startPairing({
        deviceLabel: opts.deviceLabel ?? null,
        deviceInfo: opts.deviceInfo ?? null,
      });
      session = {
        id: res.id,
        code: res.code,
        pickupSecret: res.pickupSecret,
        expiresAt: res.expiresAt,
        startedAt: Date.now(),
      };
      persistSession(session);
      emit({
        status: 'awaiting',
        session,
        error: null,
        lastIssue: null,
        retryAtMs: null,
      });
      backoffMs = pollIntervalMs;
      schedulePoll(pollIntervalMs);
    } catch (err) {
      handleNetworkFailure('start_failed', err);
    }
  }

  async function pollOnce(): Promise<void> {
    if (!session || stopped) return;
    try {
      const res = await pollPairing({
        id: session.id,
        pickupSecret: session.pickupSecret,
      });
      backoffMs = pollIntervalMs;
      switch (res.status) {
        case 'pending':
          emit({ status: 'awaiting', lastIssue: null });
          schedulePoll(pollIntervalMs);
          return;
        case 'claimed':
          emit({ status: 'claimed', lastIssue: null });
          schedulePoll(pollIntervalMs);
          return;
        case 'completed':
          if (res.token && res.screenId) {
            persistSession(null);
            const result: PairingResult = {
              screenId: res.screenId,
              playerId: res.playerId ?? null,
              tenantId: res.tenantId ?? null,
              token: res.token,
            };
            emit({ status: 'completed', result, lastIssue: null });
          } else {
            // Server says completed but we have no token. This is a
            // recoverable error — the server has wiped pending_token, so
            // start a fresh session.
            await startSession();
          }
          return;
        case 'expired':
          emit({ status: 'expired', lastIssue: null });
          // Auto-restart so the screen never lingers on a dead code.
          schedulePoll(2_000, /* asRestart */ true);
          return;
        case 'cancelled':
          emit({ status: 'cancelled', lastIssue: null });
          schedulePoll(2_000, /* asRestart */ true);
          return;
        case 'invalid':
        default:
          // Session row gone or secret mismatch. Restart from scratch.
          await startSession();
          return;
      }
    } catch (err) {
      handleNetworkFailure('poll_failed', err);
    }
  }

  function schedulePoll(ms: number, asRestart = false) {
    clearTimer();
    if (stopped) return;
    emit({ retryAtMs: Date.now() + ms });
    activeTimer = setTimeout(() => {
      activeTimer = null;
      if (stopped) return;
      if (asRestart) void startSession();
      else void pollOnce();
    }, ms);
  }

  function handleNetworkFailure(label: string, err: unknown) {
    const msg = err instanceof Error ? err.message : String(err);
    backoffMs = Math.min(MAX_BACKOFF_MS, Math.max(pollIntervalMs, backoffMs * 2));
    emit({
      status: session ? 'awaiting' : 'starting',
      lastIssue: `${label}: ${msg}`,
    });
    schedulePoll(backoffMs, !session);
  }

  // Kick off the flow. Restoring a persisted session avoids re-rendering a
  // brand new code on every reload (it's the same fragment URL the operator
  // is already photographing).
  const persisted = readPersistedSession();
  if (persisted) {
    session = persisted;
    emit({ status: 'awaiting', session, lastIssue: null });
    schedulePoll(0);
  } else {
    void startSession();
  }

  if (opts.signal) {
    if (opts.signal.aborted) {
      stopped = true;
      clearTimer();
    } else {
      opts.signal.addEventListener('abort', () => {
        stopped = true;
        clearTimer();
      });
    }
  }

  return {
    async stop() {
      stopped = true;
      clearTimer();
      if (session) {
        try {
          await cancelPairing({
            id: session.id,
            pickupSecret: session.pickupSecret,
          });
        } catch {
          /* best effort */
        }
      }
      persistSession(null);
    },
    async restart() {
      if (session) {
        try {
          await cancelPairing({
            id: session.id,
            pickupSecret: session.pickupSecret,
          });
        } catch {
          /* ignore */
        }
        session = null;
      }
      persistSession(null);
      await startSession();
    },
  };
}

/**
 * Persist the freshly received player token and navigate to the player URL.
 * Pulled out of `runPairing` so callers (the page) can decide WHEN to
 * navigate and so tests can assert on the side-effects independently.
 */
export function completePairing(result: PairingResult, opts: { reload?: boolean } = {}): string {
  writeStoredToken(result.screenId, result.token);
  // The `?t=`/`#t=` URL stays compatible with Fire TV / Signage Stick / etc
  // that may strip fragments, while the player still prefers the fragment.
  const target = buildPlayerUrl(window.location.origin, result.screenId, result.token);
  if (opts.reload !== false && typeof window !== 'undefined') {
    window.location.replace(target);
  }
  return target;
}

async function postJson<T>(path: string, body: Record<string, unknown>): Promise<T> {
  const ctrl = new AbortController();
  const timer = setTimeout(() => ctrl.abort(), FETCH_TIMEOUT_MS);
  try {
    const res = await fetch(path, {
      method: 'POST',
      credentials: 'omit',
      headers: { 'content-type': 'application/json' },
      body: JSON.stringify(body),
      signal: ctrl.signal,
    });
    if (!res.ok) {
      let detail = '';
      try {
        detail = (await res.text()).slice(0, 200);
      } catch {
        /* ignore */
      }
      throw new Error(`HTTP ${res.status}${detail ? `: ${detail}` : ''}`);
    }
    return (await res.json()) as T;
  } finally {
    clearTimeout(timer);
  }
}

async function startPairing(body: {
  deviceLabel?: string | null;
  deviceInfo?: Record<string, unknown> | null;
}): Promise<StartResponse> {
  try {
    return await postJson<StartResponse>(`${PAIR_BASE_PATH}/start`, body);
  } catch {
    // Fallback path: some deployments may have a broken /api route while
    // Supabase RPC remains reachable with anon role grants.
    const { data, error } = await supabase.rpc('start_player_pairing_session', {
      _device_label: body.deviceLabel ?? null,
      _device_info: body.deviceInfo ?? null,
      _ttl_seconds: null,
    });
    if (error) {
      throw new Error(`RPC start failed: ${error.message}`);
    }
    const obj = (data ?? {}) as Record<string, unknown>;
    if (typeof obj.error === 'string') {
      throw new Error(`RPC start failed: ${obj.error}`);
    }
    const id = typeof obj.id === 'string' ? obj.id : null;
    const code = typeof obj.code === 'string' ? obj.code : null;
    const pickupSecret = typeof obj.pickupSecret === 'string' ? obj.pickupSecret : null;
    if (!id || !code || !pickupSecret) {
      throw new Error('RPC start failed: malformed response');
    }
    return {
      id,
      code,
      pickupSecret,
      expiresAt: typeof obj.expiresAt === 'string' ? obj.expiresAt : null,
    };
  }
}

async function pollPairing(body: { id: string; pickupSecret: string }): Promise<PollResponse> {
  try {
    return await postJson<PollResponse>(`${PAIR_BASE_PATH}/poll`, body);
  } catch {
    const { data, error } = await supabase.rpc('poll_player_pairing_session', {
      _id: body.id,
      _pickup_secret: body.pickupSecret,
    });
    if (error) {
      throw new Error(`RPC poll failed: ${error.message}`);
    }
    const obj = (data ?? {}) as Record<string, unknown>;
    if (typeof obj.error === 'string') {
      if (obj.error === 'session_not_found' || obj.error === 'forbidden') {
        return { status: 'invalid' };
      }
      if (obj.error === 'missing_id' || obj.error === 'missing_secret') {
        throw new Error('RPC poll failed: bad request');
      }
      throw new Error(`RPC poll failed: ${obj.error}`);
    }
    const status = typeof obj.status === 'string' ? obj.status : null;
    if (
      status !== 'pending' &&
      status !== 'claimed' &&
      status !== 'completed' &&
      status !== 'expired' &&
      status !== 'cancelled' &&
      status !== 'invalid'
    ) {
      throw new Error('RPC poll failed: malformed response');
    }
    if (status !== 'completed') {
      return { status };
    }
    if (typeof obj.token !== 'string') {
      return { status: 'completed' };
    }
    return {
      status: 'completed',
      screenId: typeof obj.screenId === 'string' ? obj.screenId : null,
      tenantId: typeof obj.tenantId === 'string' ? obj.tenantId : null,
      playerId: typeof obj.playerId === 'string' ? obj.playerId : null,
      token: obj.token,
    };
  }
}

async function cancelPairing(body: {
  id: string;
  pickupSecret: string;
}): Promise<CancelResponse> {
  try {
    return await postJson<CancelResponse>(`${PAIR_BASE_PATH}/cancel`, body);
  } catch {
    const { data, error } = await supabase.rpc('cancel_player_pairing_session', {
      _id: body.id,
      _pickup_secret: body.pickupSecret,
    });
    if (error) {
      throw new Error(`RPC cancel failed: ${error.message}`);
    }
    const obj = (data ?? {}) as Record<string, unknown>;
    if (typeof obj.error === 'string') {
      if (obj.error === 'session_not_found' || obj.error === 'forbidden') {
        return { status: 'invalid' };
      }
      throw new Error(`RPC cancel failed: ${obj.error}`);
    }
    const status = typeof obj.status === 'string' ? obj.status : 'cancelled';
    if (status === 'completed' || status === 'expired' || status === 'cancelled' || status === 'invalid') {
      return { status };
    }
    return { status: 'cancelled' };
  }
}
